import React, { useEffect, useState } from "react";
import {
  RushOrderColors,
  RushOrderLabels,
  RushOrders as RushOrdersType,
  RushOrderType,
} from "../../types";
import { HiRefresh, HiClock } from "react-icons/hi";
import { useFetchRushOrdersQuery } from "../../store";
import Skeleton from "./Skeleton";
import RushOrdersList from "./RushOrdersList";
import classNames from "classnames";

const AUTO_REFRESH_INTERVAL = 60_000; // 60 seconds

const RushOrders: React.FC = () => {
  const { data, isFetching, refetch } = useFetchRushOrdersQuery();
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedRushOrderType, setSelectedRushOrderType] = useState<
    RushOrderType | undefined
  >();

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch]);

  // Update "Dernière mise à jour" when data changes
  useEffect(() => {
    if (data) setLastUpdated(new Date());
  }, [data]);

  const rushData: RushOrdersType | undefined = data;

  return (
    <div className="w-full bg-red-50/50 rounded-xl border border-red-100 p-6">
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-red-100 p-2 rounded-lg text-red-600">
             <HiClock className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Commandes Urgentes</h2>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              {lastUpdated
                ? `Mis à jour à ${lastUpdated.toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "En attente de données..."}
            </div>
          </div>
        </div>

        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-600 hover:text-primary hover:border-primary/30 transition-all disabled:opacity-50"
        >
          <HiRefresh className={classNames("w-4 h-4", isFetching ? "animate-spin" : "")} />
          {isFetching ? "..." : "Actualiser"}
        </button>
      </div>

      {/* Grid of Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {rushData ? (
          (Object.keys(rushData) as (keyof RushOrdersType)[]).map((key) => {
            const count = rushData[key];
            const isSelected = selectedRushOrderType === key;
            const colorClass = RushOrderColors[key] || "bg-gray-400";
            
            return (
              <div
                key={key}
                onClick={() => {
                  if (isSelected) {
                    setSelectedRushOrderType(undefined);
                  } else if (key !== RushOrderType.PriorityCount) {
                    setSelectedRushOrderType(key as RushOrderType);
                  }
                }}
                className={classNames(
                  "relative flex flex-col items-center justify-center p-3 rounded-xl border transition-all cursor-pointer select-none group",
                  isSelected 
                    ? "bg-white border-primary shadow-md scale-[1.02] ring-2 ring-primary/10" 
                    : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
                )}
              >
                <div className={classNames(
                  "text-3xl font-bold mb-1 transition-colors",
                  count > 0 ? "text-gray-900" : "text-gray-300"
                )}>
                  {count}
                </div>
                <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 text-center leading-tight">
                  {RushOrderLabels[key]}
                </div>
                
                {/* Status Indicator Dot */}
                <div className={classNames(
                  "absolute top-2 right-2 w-2 h-2 rounded-full",
                  colorClass
                )} />
              </div>
            );
          })
        ) : (
          // Skeletons
          Array.from({ length: 12 }).map((_, idx) => (
            <div key={idx} className="h-24 w-full bg-gray-100 rounded-xl animate-pulse" />
          ))
        )}
      </div>

      {/* Selected List Section */}
      {selectedRushOrderType && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
           <div className="h-px bg-red-200 w-full mb-6" />
           <RushOrdersList rushOrderType={selectedRushOrderType} />
        </div>
      )}
    </div>
  );
};

export default RushOrders;
